import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import IngredientIcon from './IngredientIcon';

/**
 * CookingPotAnimation Component
 * Dual-view cooking animation: profile view (idle/prep) and top-down view (cooking).
 * Always externally controlled via isExternallyStirring and externalCookingPhase.
 */
const CookingPotAnimation = ({
  ingredients = [],
  onCookingComplete = () => {},
  resetTrigger = 0,
  isExternallyStirring = false,
  externalCookingPhase = null, // 'prepare' | 'cooking' | 'complete'
}) => {
  const [droppedIngredients, setDroppedIngredients] = useState([]);
  const [cookingPhase, setCookingPhase] = useState('preparation');
  const [viewMode, setViewMode] = useState('profile');
  const isStirring = isExternallyStirring;

  // Dynamic liquid color shifts as ingredients are added
  const liquidColor = useMemo(() => {
    if (droppedIngredients.length === 0) return '#5a3a1a';
    const hue = 30 + droppedIngredients.length * 15;
    const lightness = Math.max(28, 55 - droppedIngredients.length * 4);
    return `hsl(${hue}, 65%, ${lightness}%)`;
  }, [droppedIngredients.length]);

  const liquidHighlight = useMemo(() => {
    if (droppedIngredients.length === 0) return '#7a5a3a';
    const hue = 35 + droppedIngredients.length * 15;
    const lightness = Math.max(38, 65 - droppedIngredients.length * 4);
    return `hsl(${hue}, 55%, ${lightness}%)`;
  }, [droppedIngredients.length]);

  // Track ingredient names we've already seen to avoid re-animating drops
  const [seenNames, setSeenNames] = useState(new Set());

  // Sync with incoming ingredients: add new ones, remove deselected ones
  useEffect(() => {
    const currentNames = new Set(ingredients.map(i => i.name));

    // Remove any dropped ingredients that are no longer in the source list
    setDroppedIngredients((prev) => prev.filter(d => currentNames.has(d.name)));

    // Find genuinely new ingredients we haven't animated yet
    const newOnes = ingredients.filter(i => !seenNames.has(i.name));
    if (newOnes.length > 0) {
      const newIngredients = newOnes.map((ingredient, index) => ({
        ...ingredient,
        id: `${ingredient.name}-${Date.now()}-${index}`,
        x: 25 + ((index * 50) % 50) + Math.random() * 20,
        y: 25 + ((index * 35) % 50) + Math.random() * 20,
        angle: (index * 137.5) % 360,
        dropIndex: index,
      }));
      setDroppedIngredients((prev) => [...prev, ...newIngredients]);
      setSeenNames((prev) => {
        const next = new Set(prev);
        newOnes.forEach(i => next.add(i.name));
        return next;
      });
    }
  }, [ingredients, seenNames]);

  // Reset
  useEffect(() => {
    if (resetTrigger > 0) {
      setDroppedIngredients([]);
      setSeenNames(new Set());
      setCookingPhase('preparation');
      setViewMode('profile');
    }
  }, [resetTrigger]);

  // Respond to external phase changes
  useEffect(() => {
    if (externalCookingPhase === 'cooking' && droppedIngredients.length > 0) {
      setCookingPhase('cooking');
      setViewMode('topdown');
    } else if (externalCookingPhase === 'prepare') {
      setCookingPhase('preparation');
      setViewMode('profile');
    }
  }, [externalCookingPhase, droppedIngredients.length]);

  const bubbleIntensity = cookingPhase === 'cooking' ? 'high'
    : droppedIngredients.length > 0 ? 'medium' : 'low';

  return (
    <motion.div
      className="relative w-80 h-96 mx-auto"
      animate={{
        rotateX: viewMode === 'topdown' ? 20 : 0,
        scale: viewMode === 'topdown' ? 1.1 : 1,
      }}
      transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
    >
      <AnimatePresence mode="wait">
        {viewMode === 'profile' ? (
          <ProfileView
            key="profile"
            liquidColor={liquidColor}
            liquidHighlight={liquidHighlight}
            droppedIngredients={droppedIngredients}
            cookingPhase={cookingPhase}
            bubbleIntensity={bubbleIntensity}
          />
        ) : (
          <TopDownView
            key="topdown"
            liquidColor={liquidColor}
            liquidHighlight={liquidHighlight}
            droppedIngredients={droppedIngredients}
            cookingPhase={cookingPhase}
            isStirring={isStirring}
            bubbleIntensity={bubbleIntensity}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Profile View ────────────────────────────────────────────────────────────

const ProfileView = ({
  liquidColor,
  liquidHighlight,
  droppedIngredients,
  cookingPhase,
  bubbleIntensity,
}) => {
  return (
    <motion.div
      className="relative w-full h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6 }}
    >
      {/* Side-view cauldron */}
      <svg
        viewBox="0 0 320 280"
        className="absolute bottom-16 left-1/2 transform -translate-x-1/2 w-64 h-56"
      >
        {/* Ambient glow under cauldron from fire */}
        <ellipse cx={160} cy={255} rx={70} ry={10} fill="rgba(255,100,0,0.15)" />

        {/* Shadow */}
        <ellipse cx={160} cy={250} rx={80} ry={15} fill="rgba(0,0,0,0.3)" />

        {/* Cauldron body */}
        <path
          d="M 80 120 Q 70 160 80 200 L 80 220 Q 80 240 100 240 L 220 240 Q 240 240 240 220 L 240 200 Q 250 160 240 120 L 230 120 Q 220 100 200 100 L 120 100 Q 100 100 90 120 Z"
          fill="#2a2a2a"
          stroke="#1a1a1a"
          strokeWidth="3"
        />

        {/* Subtle metallic sheen on the body */}
        <path
          d="M 95 130 Q 90 170 95 210"
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="8"
          strokeLinecap="round"
        />

        {/* Cauldron rim */}
        <ellipse cx={160} cy={120} rx={80} ry={12} fill="#3a3a3a" stroke="#4a4a4a" strokeWidth="2" />

        {/* Rim highlight */}
        <ellipse cx={160} cy={118} rx={60} ry={5} fill="rgba(255,255,255,0.04)" />

        {/* Legs */}
        <g>
          <rect x={120} y={240} width={8} height={18} fill="#1a1a1a" rx={4} />
          <rect x={155} y={240} width={8} height={18} fill="#1a1a1a" rx={4} />
          <rect x={190} y={240} width={8} height={18} fill="#1a1a1a" rx={4} />
        </g>

        {/* Handles */}
        <g>
          <ellipse cx={70} cy={160} rx={12} ry={25} fill="none" stroke="#1a1a1a" strokeWidth="6" />
          <ellipse cx={250} cy={160} rx={12} ry={25} fill="none" stroke="#1a1a1a" strokeWidth="6" />
        </g>

        {/* Liquid surface */}
        <motion.g
          animate={{
            scaleY: [1, 1.08, 0.95, 1.04, 1],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: '160px 120px' }}
        >
          <ellipse
            cx={160}
            cy={120}
            rx={75}
            ry={10}
            fill={liquidColor}
          />
        </motion.g>

        {/* Surface shimmer */}
        <motion.g
          animate={{
            x: [0, 20, 0],
            opacity: [0.08, 0.15, 0.08],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ellipse
            cx={150}
            cy={118}
            rx={30}
            ry={4}
            fill="rgba(255,255,255,0.08)"
          />
        </motion.g>

        {/* Liquid depth shadow */}
        <ellipse cx={160} cy={122} rx={70} ry={8} fill="rgba(0,0,0,0.3)" />
      </svg>

      {/* Fire base - rendered after cauldron SVG so flames are in front */}
      <FireBase />

      {/* Ingredient drops + settled icons on liquid */}
      <div className="absolute inset-0 pointer-events-none">
        <AnimatePresence>
          {droppedIngredients.map((ingredient) => (
            <ProfileIngredient key={ingredient.id} ingredient={ingredient} />
          ))}
        </AnimatePresence>
      </div>

      {/* Steam */}
      <div className="absolute top-12 left-1/2 transform -translate-x-1/2 w-48 h-24 pointer-events-none">
        <SteamLayer count={cookingPhase === 'cooking' ? 8 : 4} speed={cookingPhase === 'cooking' ? 1.5 : 2.5} />
      </div>

      {/* Bubbles at rim */}
      <div className="absolute top-24 left-1/2 transform -translate-x-1/2 w-40 h-12 pointer-events-none">
        <RimBubbles count={bubbleIntensity === 'high' ? 8 : 4} active={droppedIngredients.length > 0} />
      </div>
    </motion.div>
  );
};

// ─── Top-Down View ───────────────────────────────────────────────────────────

const TopDownView = ({
  liquidColor,
  liquidHighlight,
  droppedIngredients,
  cookingPhase,
  isStirring,
  bubbleIntensity,
}) => {
  const stirDuration = 2;
  const bubbleCount = bubbleIntensity === 'high' ? 14 : bubbleIntensity === 'medium' ? 8 : 4;

  return (
    <motion.div
      className="relative w-full h-full"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.6 }}
    >
      {/* Cauldron circle */}
      <svg viewBox="0 0 320 320" className="absolute inset-0 w-full h-full">
        {/* Outer rim */}
        <circle cx={160} cy={160} r={140} fill="#2a2a2a" stroke="#1a1a1a" strokeWidth="8" />
        {/* Inner wall */}
        <circle cx={160} cy={160} r={130} fill="#3a3a3a" stroke="#4a4a4a" strokeWidth="4" />

        {/* Liquid */}
        <motion.g
          animate={{
            scale: isStirring ? [1, 0.975, 1.01, 0.985, 1] : 1,
          }}
          transition={{
            duration: stirDuration,
            repeat: isStirring ? Infinity : 0,
            ease: 'easeInOut',
          }}
          style={{ transformOrigin: '160px 160px' }}
        >
          <circle
            cx={160}
            cy={160}
            r={115}
            fill={liquidColor}
          />
        </motion.g>

        {/* Liquid depth ring */}
        <circle cx={160} cy={160} r={108} fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="6" />

        {/* Concentric ripples when stirring */}
        {isStirring && (
          <g>
            {[0, 1, 2].map((i) => (
              <motion.g
                key={`ripple-${i}`}
                animate={{
                  scale: [(30 + i * 15) / 80, (100 + i * 10) / 80],
                  opacity: [0.12, 0],
                }}
                transition={{
                  duration: stirDuration * 0.8,
                  repeat: Infinity,
                  delay: i * (stirDuration * 0.25),
                  ease: 'easeOut',
                }}
                style={{ transformOrigin: '160px 160px' }}
              >
                <circle
                  cx={160}
                  cy={160}
                  r={80}
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="1.5"
                />
              </motion.g>
            ))}
          </g>
        )}

        {/* Vortex spiral when stirring */}
        {isStirring && (
          <motion.g
            animate={{ rotate: [0, 360] }}
            transition={{ duration: stirDuration, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '160px 160px' }}
          >
            <path
              d="M 160 160 Q 175 145 190 155 Q 178 170 160 160"
              fill="none"
              stroke="rgba(255,255,255,0.07)"
              strokeWidth="2"
            />
            <path
              d="M 160 160 Q 145 175 130 165 Q 142 150 160 160"
              fill="none"
              stroke="rgba(255,255,255,0.07)"
              strokeWidth="2"
            />
          </motion.g>
        )}

        {/* Surface shimmer spot */}
        <motion.g
          animate={{
            x: isStirring ? [0, 30, 0] : [0, 10, 0],
            y: isStirring ? [0, 20, 0] : [0, 5, 0],
            opacity: [0.04, 0.1, 0.04],
          }}
          transition={{ duration: isStirring ? stirDuration : 5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ellipse
            cx={145}
            cy={145}
            rx={20}
            ry={12}
            fill="rgba(255,255,255,0.06)"
          />
        </motion.g>
      </svg>

      {/* Floating ingredients */}
      <div className="absolute top-8 left-8 w-64 h-64 overflow-hidden">
        <AnimatePresence>
          {droppedIngredients.map((ingredient) => (
            <FloatingIngredient
              key={ingredient.id}
              ingredient={ingredient}
              isStirring={isStirring}
              stirDuration={stirDuration}
              isCooking={cookingPhase === 'cooking'}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Stirring spoon */}
      {isStirring && <StirringSpoon duration={stirDuration} />}

      {/* Top-down bubbles */}
      <div className="absolute top-8 left-8 w-64 h-64 overflow-hidden pointer-events-none">
        <TopDownBubbles count={bubbleCount} active={cookingPhase === 'cooking'} />
      </div>
    </motion.div>
  );
};

// ─── Shared Sub-Components ───────────────────────────────────────────────────

/** Teardrop flames arranged in an ellipse under the cauldron */
const FireBase = () => {
  // 8 flames distributed around an ellipse
  const flames = useMemo(() => {
    const count = 8;
    const cx = 100; // center of 200-wide viewBox
    const cy = 0;   // vertical center of ellipse
    const rx = 55;  // horizontal radius
    const ry = 8;   // vertical radius (perspective squash)
    return Array.from({ length: count }, (_, i) => {
      // Distribute around the bottom half of the ellipse (PI to 2*PI = front-facing arc)
      // Plus a few on top for the back
      const angle = (i / count) * Math.PI * 2;
      return {
        id: i,
        cx: cx + rx * Math.cos(angle),
        cy: cy + ry * Math.sin(angle),
        // Flames at the back (top of ellipse) are smaller and dimmer
        isBack: Math.sin(angle) < -0.2,
        delay: i * 0.09,
        duration: 0.7 + Math.random() * 0.3,
      };
    });
  }, []);

  return (
    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 w-48 h-16">
      {/* Ember glow ellipse */}
      <motion.div
        className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-36 h-6 rounded-full blur-md"
        style={{ background: 'radial-gradient(ellipse, rgba(255,80,0,0.5), transparent)' }}
        animate={{ opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* SVG teardrop flames on ellipse */}
      <svg
        viewBox="0 0 200 50"
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-44 h-16"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="flameGradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#FF4500" />
            <stop offset="40%" stopColor="#FF6347" />
            <stop offset="75%" stopColor="#FFA500" />
            <stop offset="100%" stopColor="#FFD700" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="flameCoreGradient" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#FF6347" />
            <stop offset="50%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFFACD" stopOpacity="0.8" />
          </linearGradient>
        </defs>

        {/* Render back flames first, then front flames on top */}
        {flames.filter(f => f.isBack).map((f) => (
          <FlameTeardrop key={f.id} f={f} scale={0.7} opacity={0.5} />
        ))}
        {flames.filter(f => !f.isBack).map((f) => (
          <FlameTeardrop key={f.id} f={f} scale={1} opacity={1} />
        ))}
      </svg>
    </div>
  );
};

/** Single teardrop flame at a given position */
const FlameTeardrop = ({ f, scale = 1, opacity = 1 }) => {
  const s = scale;
  const x = f.cx;
  const y = f.cy;
  return (
    <g opacity={opacity}>
      {/* Glow */}
      <motion.path
        d={`M ${x} ${y - 28 * s} Q ${x - 8 * s} ${y - 10 * s} ${x - 7 * s} ${y + 2} Q ${x} ${y + 6} ${x + 7 * s} ${y + 2} Q ${x + 8 * s} ${y - 10 * s} ${x} ${y - 28 * s} Z`}
        fill="rgba(255,100,0,0.4)"
        style={{ transformOrigin: `${x}px ${y + 2}px`, filter: 'blur(2px)' }}
        animate={{
          scaleY: [1, 1.25, 0.9, 1.15, 1],
          scaleX: [1, 0.85, 1.1, 0.9, 1],
        }}
        transition={{ duration: f.duration + 0.1, repeat: Infinity, delay: f.delay, ease: 'easeInOut' }}
      />
      {/* Main teardrop */}
      <motion.path
        d={`M ${x} ${y - 24 * s} Q ${x - 6 * s} ${y - 8 * s} ${x - 5 * s} ${y + 2} Q ${x} ${y + 5} ${x + 5 * s} ${y + 2} Q ${x + 6 * s} ${y - 8 * s} ${x} ${y - 24 * s} Z`}
        fill="url(#flameGradient)"
        style={{ transformOrigin: `${x}px ${y + 2}px` }}
        animate={{
          scaleY: [1, 1.2, 0.85, 1.15, 1],
          scaleX: [1, 0.8, 1.1, 0.9, 1],
        }}
        transition={{ duration: f.duration, repeat: Infinity, delay: f.delay, ease: 'easeInOut' }}
      />
      {/* Bright core */}
      <motion.path
        d={`M ${x} ${y - 16 * s} Q ${x - 3 * s} ${y - 6 * s} ${x - 3 * s} ${y} Q ${x} ${y + 3} ${x + 3 * s} ${y} Q ${x + 3 * s} ${y - 6 * s} ${x} ${y - 16 * s} Z`}
        fill="url(#flameCoreGradient)"
        style={{ transformOrigin: `${x}px ${y}px` }}
        animate={{ scaleY: [1, 1.3, 0.9, 1.2, 1] }}
        transition={{ duration: f.duration * 0.8, repeat: Infinity, delay: f.delay + 0.05, ease: 'easeInOut' }}
      />
    </g>
  );
};

/** Ingredient that drops into the cauldron then floats inside the liquid */
const ProfileIngredient = ({ ingredient }) => {
  const [settled, setSettled] = useState(false);
  // Spread across the liquid surface horizontally (within the ellipse bounds)
  const settledX = useMemo(() => -20 + ((ingredient.angle || 0) % 150) / 150 * 40, [ingredient.angle]);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), (ingredient.dropIndex || 0) * 400 + 900);
    return () => clearTimeout(timer);
  }, [ingredient.dropIndex]);

  if (settled) {
    // Floating inside the liquid - positioned just below the rim line
    return (
      <motion.div
        className="absolute"
        style={{
          left: `calc(50% + ${settledX}px)`,
          // Position right in the green liquid ellipse
          top: 'calc(48% - 2px)',
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{
          scale: 0.65,
          opacity: 0.75, // slightly transparent = submerged look
          y: [0, -1.5, 0, 1.5, 0],
          x: [-1, 1, -1], // gentle lateral drift
        }}
        transition={{
          scale: { duration: 0.3 },
          opacity: { duration: 0.3 },
          y: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
          x: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
        }}
      >
        <IngredientIcon ingredientName={ingredient.name} size={18} glowing />
      </motion.div>
    );
  }

  // Falling animation - drops to the liquid surface
  return (
    <motion.div
      className="absolute left-1/2 top-8"
      initial={{ y: -50, x: -15 + Math.random() * 30, scale: 0, rotate: 0 }}
      animate={{
        y: 85,
        scale: [0, 1.2, 0.8, 0.3],
        rotate: [0, 180 + Math.random() * 180],
      }}
      transition={{
        duration: 0.9,
        ease: [0.55, 0, 1, 0.45],
        delay: (ingredient.dropIndex || 0) * 0.4,
      }}
    >
      <IngredientIcon ingredientName={ingredient.name} size={32} glowing />
    </motion.div>
  );
};

/** Multi-layer steam wisps */
const SteamLayer = ({ count, speed }) => {
  const wisps = useMemo(() => (
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 15 + (i * (70 / count)) + Math.random() * 10,
      size: 2 + Math.random() * 2,
      delay: i * (speed / count),
      drift: (Math.random() - 0.5) * 20,
    }))
  ), [count, speed]);

  return (
    <>
      {wisps.map((w) => (
        <motion.div
          key={w.id}
          className="absolute rounded-full"
          style={{
            left: `${w.x}%`,
            bottom: 0,
            width: w.size,
            height: w.size * 3,
            background: 'rgba(255,255,255,0.35)',
            filter: 'blur(2px)',
          }}
          animate={{
            y: [-5, -55],
            x: [0, w.drift],
            opacity: [0, 0.35, 0],
            scaleX: [1, 2.5],
            scaleY: [1, 0.6],
          }}
          transition={{
            duration: speed,
            repeat: Infinity,
            delay: w.delay,
            ease: 'easeOut',
          }}
        />
      ))}
    </>
  );
};

/** Small bubbles popping at the rim line in profile view */
const RimBubbles = ({ count, active }) => {
  const bubbles = useMemo(() => (
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 20 + Math.random() * 60,
      size: 2 + Math.random() * 3,
      delay: Math.random() * 2,
    }))
  ), [count]);

  if (!active) return null;

  return (
    <>
      {bubbles.map((b) => (
        <motion.div
          key={b.id}
          className="absolute bg-white rounded-full"
          style={{
            width: b.size,
            height: b.size,
            left: `${b.x}%`,
            bottom: 4,
            opacity: 0,
          }}
          animate={{
            y: [0, -12, -18],
            opacity: [0, 0.6, 0],
            scale: [0.5, 1, 0.3],
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: b.delay,
            ease: 'easeOut',
          }}
        />
      ))}
    </>
  );
};

/** Floating ingredient in top-down view with orbital drift */
const FloatingIngredient = ({ ingredient, isStirring, stirDuration, isCooking }) => {
  const orbitRadius = 12;
  const angleRad = ((ingredient.angle || 0) * Math.PI) / 180;

  return (
    <motion.div
      className="absolute"
      style={{
        left: `${ingredient.x || 50}%`,
        top: `${ingredient.y || 50}%`,
      }}
      initial={{ scale: 0, rotate: 0 }}
      animate={{
        scale: 1,
        rotate: isStirring ? [0, 360] : 0,
        x: isStirring
          ? [0, Math.sin(angleRad) * orbitRadius, 0, -Math.sin(angleRad) * orbitRadius, 0]
          : [0, 2, 0, -2, 0],
        y: isStirring
          ? [0, Math.cos(angleRad) * orbitRadius * 0.7, 0, -Math.cos(angleRad) * orbitRadius * 0.7, 0]
          : [0, 1.5, 0, -1.5, 0],
      }}
      transition={{
        scale: { duration: 0.8 },
        rotate: { duration: stirDuration, repeat: isStirring ? Infinity : 0, ease: 'linear' },
        x: { duration: isStirring ? stirDuration : 4, repeat: Infinity, ease: 'easeInOut' },
        y: { duration: isStirring ? stirDuration : 4, repeat: Infinity, ease: 'easeInOut' },
      }}
    >
      <IngredientIcon
        ingredientName={ingredient.name}
        size={36}
        animate={isStirring}
        glowing={isCooking}
      />
    </motion.div>
  );
};

/** Wooden stirring spoon with orbital motion */
const StirringSpoon = ({ duration }) => (
  <motion.div
    className="absolute top-1/2 left-1/2 z-10"
    style={{ transform: 'translate(-50%, -50%)' }}
    animate={{ rotate: [0, 360] }}
    transition={{ duration, repeat: Infinity, ease: 'linear' }}
  >
    <motion.div
      className="absolute w-3 h-24"
      style={{
        left: 40,
        top: -12,
        background: 'linear-gradient(0deg, #6B3410 0%, #8B5E34 40%, #C4956A 100%)',
        borderRadius: 2,
        transformOrigin: 'center',
        boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
      }}
      animate={{ rotate: [0, -360] }}
      transition={{ duration, repeat: Infinity, ease: 'linear' }}
    >
      {/* Spoon bowl */}
      <div
        className="absolute -top-4 -left-4 w-10 h-8 rounded-full border-2 border-amber-900"
        style={{
          background: 'linear-gradient(135deg, #C4956A 0%, #DEB887 50%, #F5DEB3 100%)',
          transform: 'rotate(15deg)',
        }}
      />
    </motion.div>
  </motion.div>
);

/** Bubbles appearing within the circular top-down liquid */
const TopDownBubbles = ({ count, active }) => {
  const bubbles = useMemo(() => (
    Array.from({ length: count }, (_, i) => {
      const angle = Math.random() * 2 * Math.PI;
      const dist = Math.random() * 0.75;
      const r = 35;
      return {
        id: i,
        size: 3 + Math.random() * 6,
        x: Math.max(15, Math.min(85, 50 + r * dist * Math.cos(angle))),
        y: Math.max(15, Math.min(85, 50 + r * dist * Math.sin(angle))),
        delay: Math.random() * 2.5,
        duration: 1.5 + Math.random() * 1.5,
      };
    })
  ), [count]);

  if (!active) return null;

  return (
    <>
      {bubbles.map((b) => (
        <motion.div
          key={b.id}
          className="absolute bg-white rounded-full"
          style={{
            width: b.size,
            height: b.size,
            left: `${b.x}%`,
            top: `${b.y}%`,
            opacity: 0,
          }}
          animate={{
            scale: [0, 1, 1.2, 0],
            opacity: [0, 0.5, 0.6, 0],
          }}
          transition={{
            duration: b.duration,
            repeat: Infinity,
            delay: b.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </>
  );
};

export default CookingPotAnimation;
